import { useRef, useState } from "react";
import { ImageIcon } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useQuitarImagenDelExpediente, useSubirImagenDelExpediente } from "@/hooks/useInformes";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import { urlDeLaImagenDelExpediente, type ImagenDeInforme } from "@/lib/informes";
import { cn } from "@/lib/utils";
import type { FotoDeTerreno } from "@/lib/terreno";

import { ImagenDeTerreno, UbicacionDeLaFotoDeTerreno } from "./FotosDeTerreno";
import { CLASE_DE_FOTO_RESALTADA } from "./MarcasDeSincronizacion";
import { useResaltadoDeLaCaptura } from "./PanelDeTerrenoDeLaDescripcion";
import { horaDeCaptura, momentoDeSincronizacion } from "./registroDeTerreno";
import { motivoDeRechazoDeLaImagen } from "./TarjetaDeImagen";

interface PortadaDeTerrenoProps {
  informeId: number;
  /** La portada que mandó el campo, o `null` si todavía no llegó. */
  portada: FotoDeTerreno | null;
  /** La portada subida a mano, o `null`. Si existe, gana sobre la de terreno. */
  manual?: ImagenDeInforme | null;
  /** Quién la envió, si se sabe. */
  encargado: string | null;
  onAbrir: (uuid: string) => void;
  /** Sube la fotografía elegida. Se guarda al soltarla, no con "Guardar cambios". */
  onSubir: (archivo: File) => void;
  /** Quita la manual para que vuelva a verse la de la visita. */
  onUsarLaDeTerreno: () => void;
  ocupado?: boolean;
  error?: string | null;
}

/**
 * La "Fotografía de portada" dentro de la tarjeta de imágenes.
 *
 * LA REGLA: una portada subida a mano GANA; sin ella se usa la de terreno.
 * "Usar la foto de la visita en terreno" simplemente BORRA la manual, así que
 * no hay una columna de "origen": la presencia de la fila lo dice todo.
 *
 * Es presentacional: el que sube es {@link PortadaDelInforme}. No reusa
 * `ZonaDeImagen` porque aquella escucha el pegado en TODO el documento y está
 * escrita para la imagen satelital: dos montadas a la vez se pisarían.
 */
export function PortadaDeTerreno({
  informeId,
  portada,
  manual = null,
  encargado,
  onAbrir,
  onSubir,
  onUsarLaDeTerreno,
  ocupado = false,
  error = null,
}: PortadaDeTerrenoProps) {
  const archivo = portada?.archivo ?? null;
  const marca = useResaltadoDeLaCaptura(portada?.sincronizacion_id);
  const input = useRef<HTMLInputElement>(null);
  const [rechazo, setRechazo] = useState<string | null>(null);
  const hay = manual !== null || portada !== null;

  function elegir(elegido: File | undefined) {
    if (!elegido) return;
    const motivo = motivoDeRechazoDeLaImagen(elegido);
    setRechazo(motivo);
    if (!motivo) onSubir(elegido);
  }

  const mensaje = rechazo ?? error;
  const urlManual = manual ? urlDeLaImagenDelExpediente(informeId, "portada", manual.hash) : null;

  return (
    <div className="border-divisor mt-5 border-t pt-4">
      <div className="flex items-center gap-2.5">
        <div className="min-w-0 flex-1">
          <h3 className="text-ink text-[0.8125rem] font-bold">Fotografía de portada</h3>
          <p className="text-ink-muted mt-0.5 text-xs font-semibold">
            {manual
              ? "La subiste tú: se usa en lugar de la de la visita."
              : portada
                ? "Llegó con la visita en terreno. Puedes reemplazarla si prefieres otra toma."
                : "La envía el encargado desde la App de terreno. También puedes subirla aquí."}
          </p>
        </div>
        <span
          className={cn(
            "inline-flex flex-none items-center gap-1.5 rounded-sm px-2.5 py-1 text-[0.6875rem] font-bold",
            manual
              ? "bg-info-soft text-info-soft-foreground"
              : portada
                ? "bg-success-soft text-success-soft-foreground"
                : "bg-warning-soft text-warning-soft-foreground",
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              "size-[7px] rounded-full",
              manual ? "bg-info-dot" : portada ? "bg-success-dot" : "bg-warning-dot",
            )}
          />
          {manual ? "Subida manualmente" : portada ? "Desde terreno" : "Pendiente de terreno"}
        </span>
      </div>

      {/* `sr-only` y no `display:none`, para que siga existiendo para la
          tecnología asistiva. Es `type="file"`, así que no envía el form. */}
      <input
        ref={input}
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        className="sr-only"
        aria-label="Archivo de la fotografía de portada"
        onChange={(e) => {
          const elegido = e.target.files?.[0];
          e.target.value = "";
          elegir(elegido);
        }}
      />

      {manual && urlManual ? (
        <div className="mt-3 grid items-start gap-4 sm:grid-cols-[minmax(0,21rem)_minmax(0,1fr)]">
          <a
            href={urlManual}
            target="_blank"
            rel="noreferrer"
            title="Ver en tamaño completo"
            className="bg-muted hover:ring-primary focus-visible:ring-ring/50 relative block aspect-[16/10] overflow-hidden rounded-xl outline-none hover:ring-2 focus-visible:ring-[3px]"
          >
            <img
              key={manual.hash}
              src={urlManual}
              alt="Fotografía de portada"
              className="size-full object-cover"
            />
          </a>
          <div className="flex flex-col items-start gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={ocupado}
              onClick={() => input.current?.click()}
            >
              {ocupado ? <Spinner /> : null}
              Reemplazar fotografía
            </Button>
            {portada ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={ocupado}
                onClick={onUsarLaDeTerreno}
              >
                Usar la foto de la visita en terreno
              </Button>
            ) : null}
          </div>
        </div>
      ) : portada && archivo ? (
        <div className="mt-3 grid items-start gap-4 sm:grid-cols-[minmax(0,21rem)_minmax(0,1fr)]">
          <button
            {...marca.atributo}
            type="button"
            onClick={() => onAbrir(portada.uuid)}
            aria-label="Ampliar fotografía · Antecedentes generales · Portada"
            title="Ampliar"
            className={cn(
              "bg-muted hover:ring-primary focus-visible:ring-ring/50 relative block aspect-[16/10] overflow-hidden rounded-xl outline-none hover:ring-2 focus-visible:ring-[3px]",
              marca.on && CLASE_DE_FOTO_RESALTADA,
            )}
          >
            <ImagenDeTerreno
              informeId={informeId}
              uuid={portada.uuid}
              hash={archivo.hash}
              mime={archivo.mime}
              alt="Fotografía de portada · Antecedentes generales"
            />
            <span className="bg-card/90 text-ink-body absolute bottom-2 left-2 rounded-sm px-1.5 py-0.5 font-mono text-[0.65625rem] font-bold">
              Capturada {horaDeCaptura(portada.capturado_en)}
            </span>
          </button>

          <div className="text-ink-body flex flex-col items-start gap-1 text-xs font-semibold">
            <span>
              Enviada
              {encargado ? (
                <>
                  {" "}
                  por <span className="text-ink font-bold">{encargado}</span>
                </>
              ) : null}{" "}
              desde la App de terreno.
            </span>
            <span className="text-ink-muted font-mono text-[0.6875rem]">
              Sincronizada {momentoDeSincronizacion(portada.sincronizado_en)}
            </span>
            <UbicacionDeLaFotoDeTerreno latitud={portada.latitud} longitud={portada.longitud} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={ocupado}
              onClick={() => input.current?.click()}
              className="mt-1"
            >
              {ocupado ? <Spinner /> : null}
              Subir otra fotografía
            </Button>
          </div>
        </div>
      ) : null}

      {!hay ? (
        <div className="mt-3">
          <button
            type="button"
            disabled={ocupado}
            onClick={() => input.current?.click()}
            aria-label="Subir fotografía de portada"
            className="border-input hover:border-primary/50 hover:bg-accent/40 focus-visible:ring-ring/50 flex w-full flex-col items-center gap-1.5 rounded-xl border-[1.5px] border-dashed p-5 transition-colors outline-none focus-visible:ring-[3px] disabled:opacity-60"
          >
            <span className="bg-muted text-ink-muted flex size-9 items-center justify-center rounded-full">
              {ocupado ? <Spinner /> : <ImageIcon className="size-4" aria-hidden="true" />}
            </span>
            <span className="text-ink-body text-xs font-semibold">
              Haz clic para subirla tú · JPG, PNG o WEBP · hasta 10 MB
            </span>
          </button>
          <p className="text-ink-hint mt-2 text-xs">
            Si el encargado la envía desde terreno, se usará esa, salvo que subas una propia.
          </p>
        </div>
      ) : null}

      {mensaje ? <p className="text-destructive mt-2 text-sm">{mensaje}</p> : null}
    </div>
  );
}

/**
 * {@link PortadaDeTerreno} conectada a la API: sube y quita la portada manual
 * al momento, igual que la imagen satelital (no entra en "Guardar cambios").
 */
export function PortadaDelInforme(
  props: Omit<PortadaDeTerrenoProps, "onSubir" | "onUsarLaDeTerreno" | "ocupado" | "error">,
) {
  const subir = useSubirImagenDelExpediente(props.informeId);
  const quitar = useQuitarImagenDelExpediente(props.informeId);
  const [error, setError] = useState<string | null>(null);

  return (
    <PortadaDeTerreno
      {...props}
      ocupado={subir.isPending || quitar.isPending}
      error={error}
      onSubir={(archivo) => {
        setError(null);
        subir.mutate(
          { tipo: "portada", archivo },
          {
            onSuccess: () => toast.success("Fotografía de portada guardada."),
            onError: (e) =>
              setError(
                parseValidationErrors(e)?.imagen ??
                  getApiErrorMessage(e, "No pudimos guardar la fotografía. Vuelve a intentarlo."),
              ),
          },
        );
      }}
      onUsarLaDeTerreno={() => {
        setError(null);
        quitar.mutate("portada", {
          onSuccess: () => toast.success("Se usará la foto de la visita en terreno."),
          onError: (e) => setError(getApiErrorMessage(e, "No pudimos quitar la fotografía.")),
        });
      }}
    />
  );
}
